import { useState } from 'react';
import { X, Layout, Wand2, MessageCircle, Lightbulb } from 'lucide-react';
import TemplateGallery from './TemplateGallery';
import FunnelWizard from './FunnelWizard';

const GUIDANCE_MODES = [
    {
        id: 'wizard',
        icon: Wand2,
        label: 'Step-by-Step',
        description: 'Guided creation'
    },
    {
        id: 'templates',
        icon: Layout,
        label: 'Templates',
        description: 'Pre-built funnels'
    }
];

export default function GuidanceHub({
    isOpen,
    onClose,
    onApplyTemplate,
    onWizardComplete,
    nodes,
    edges
}) {
    const [activeMode, setActiveMode] = useState(null);

    if (!isOpen) return null;

    const handleTemplateSelect = (template) => {
        onApplyTemplate(template);
        setActiveMode(null);
        onClose();
    };

    const handleWizardComplete = (data) => {
        onWizardComplete(data);
        setActiveMode(null);
        onClose();
    };

    // Show sub-modals
    if (activeMode === 'templates') {
        return (
            <TemplateGallery
                isOpen={true}
                onClose={() => setActiveMode(null)}
                onSelectTemplate={handleTemplateSelect}
            />
        );
    }

    if (activeMode === 'wizard') {
        return (
            <FunnelWizard
                isOpen={true}
                onClose={() => setActiveMode(null)}
                onComplete={handleWizardComplete}
            />
        );
    }

    // Main hub modal
    return (
        <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
        }}>
            <div style={{
                backgroundColor: 'var(--color-surface)',
                borderRadius: 'var(--radius-lg)',
                width: '90%',
                maxWidth: '500px',
                boxShadow: '0 20px 40px rgba(0, 0, 0, 0.2)'
            }}>
                {/* Header */}
                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: 'var(--space-4) var(--space-6)',
                    borderBottom: '1px solid var(--color-border)'
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                        <Lightbulb size={24} style={{ color: 'var(--color-primary)' }} />
                        <div>
                            <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 600 }}>
                                Get Help
                            </h2>
                            <p style={{ margin: 0, fontSize: '0.875rem', color: 'var(--color-text-secondary)' }}>
                                Choose how you'd like to build your funnel
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        style={{
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            padding: 'var(--space-2)',
                            color: 'var(--color-text-tertiary)'
                        }}
                    >
                        <X size={20} />
                    </button>
                </div>

                {/* Options */}
                <div style={{
                    padding: 'var(--space-6)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 'var(--space-3)'
                }}>
                    {GUIDANCE_MODES.map(mode => {
                        const Icon = mode.icon;
                        return (
                            <button
                                key={mode.id}
                                onClick={() => setActiveMode(mode.id)}
                                style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 'var(--space-4)',
                                    padding: 'var(--space-4)',
                                    border: '1px solid var(--color-border)',
                                    borderRadius: 'var(--radius-lg)',
                                    backgroundColor: 'var(--color-surface)',
                                    cursor: 'pointer',
                                    textAlign: 'left',
                                    transition: 'all 0.2s ease'
                                }}
                                className="guidance-option"
                            >
                                <div style={{
                                    width: '48px',
                                    height: '48px',
                                    borderRadius: 'var(--radius-md)',
                                    backgroundColor: 'var(--primary-100)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    flexShrink: 0
                                }}>
                                    <Icon size={24} style={{ color: 'var(--color-primary)' }} />
                                </div>
                                <div>
                                    <h3 style={{
                                        margin: '0 0 var(--space-1) 0',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        color: 'var(--color-text-primary)'
                                    }}>
                                        {mode.label}
                                    </h3>
                                    <p style={{
                                        margin: 0,
                                        fontSize: '0.85rem',
                                        color: 'var(--color-text-secondary)'
                                    }}>
                                        {mode.description}
                                    </p>
                                </div>
                            </button>
                        );
                    })}

                    {/* Quick tips section */}
                    <div style={{
                        marginTop: 'var(--space-2)',
                        padding: 'var(--space-4)',
                        backgroundColor: 'var(--color-bg-secondary)',
                        borderRadius: 'var(--radius-md)'
                    }}>
                        <h4 style={{
                            margin: '0 0 var(--space-2) 0',
                            fontSize: '0.85rem',
                            fontWeight: 600,
                            color: 'var(--color-text-primary)'
                        }}>
                            Quick Tips
                        </h4>
                        <ul style={{
                            margin: 0,
                            paddingLeft: 'var(--space-4)',
                            fontSize: '0.8rem',
                            color: 'var(--color-text-secondary)',
                            lineHeight: 1.6
                        }}>
                            <li>Drag nodes from the left panel onto the canvas</li>
                            <li>Connect nodes by dragging from the handles</li>
                            <li>Double-click a node to edit its settings</li>
                            <li>Use the AI Assistant for personalized help</li>
                        </ul>
                    </div>
                </div>

                <style>{`
                    .guidance-option:hover {
                        border-color: var(--color-primary) !important;
                        background-color: var(--primary-50) !important;
                    }
                `}</style>
            </div>
        </div>
    );
}
